import { inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ANALYTICS_CATEGORY, CONSENT_COOKIE_NAME, getConsentCookieDomain } from '@sb-shared/helpers/consent-cookie';
import { CurrentLangService } from '@sb-shared/services/current-lang.service';
import { BehaviorSubject } from 'rxjs';
import * as CookieConsent from 'vanilla-cookieconsent';

/** Language key the banner text is registered under until the user's language changes. */
const INITIAL_LANGUAGE_KEY = 'sb';

const escapeHtml = (text: string): string =>
  String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Wraps the self-hosted cookieconsent library (SB-17045). Trackers subscribe to `analyticsConsent$` and start
 * on the first `true`, so consent given later takes effect without a reload.
 *
 * IdentityServer runs the same library with the same cookie and categories, from
 * SchoolsBuddy.IdentityServer/wwwroot/js/cookie-consent.js.
 */
@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  /** Whether the user has accepted the Analytics category. */
  readonly analyticsConsent$ = new BehaviorSubject<boolean>(false);

  /** The library, held on the instance so specs can replace it. */
  protected lib: typeof CookieConsent = CookieConsent;

  private config: CookieConsent.CookieConsentConfig;
  private runPromise: Promise<void>;
  private resolveLabels: (translation: CookieConsent.Translation) => void;
  private bannerRequested = false;
  private isRtl = false;

  // Resolved on first use, so that injecting this service (UserService -> PendoService does) pulls in no
  // translation providers.
  private readonly injector = inject(Injector);

  private get translate(): TranslateService {
    return this.injector.get(TranslateService);
  }

  private get currentLangService(): CurrentLangService {
    return this.injector.get(CurrentLangService);
  }

  /** Starts the library and seeds `analyticsConsent$` from any stored choice. Safe to call more than once. */
  init(): void {
    if (this.runPromise) {
      return;
    }

    const labelsReady = new Promise<CookieConsent.Translation>(resolve => (this.resolveLabels = resolve));

    this.config = {
      autoShow: false,
      hideFromBots: false,
      manageScriptTags: false,
      cookie: {
        name: CONSENT_COOKIE_NAME,
        domain: getConsentCookieDomain(location.hostname) ?? location.hostname
      },
      guiOptions: {
        consentModal: { equalWeightButtons: true },
        preferencesModal: { equalWeightButtons: true }
      },
      categories: {
        necessary: { enabled: true, readOnly: true },
        [ANALYTICS_CATEGORY]: {
          autoClear: {
            cookies: [{ name: /^_ga/ }, { name: /^_gid/ }, { name: /^_clck/ }, { name: /^_clsk/ }, { name: /^_pendo_/ }]
          }
        }
      },
      language: {
        default: INITIAL_LANGUAGE_KEY,
        rtl: [],
        translations: { [INITIAL_LANGUAGE_KEY]: () => labelsReady }
      },
      onConsent: () => this.publishConsent(),
      onChange: () => this.publishConsent()
    };

    this.runPromise = this.lib.run(this.config);

    // run() reads the stored cookie synchronously, before it waits for the banner text.
    this.publishConsent();
  }

  /** Supplies the banner text from the org labels and shows the banner when no valid choice is stored. */
  showBanner(): void {
    if (!this.runPromise || this.bannerRequested) {
      return;
    }

    this.bannerRequested = true;

    this.currentLangService.currentLang.subscribe(language => {
      this.isRtl = language?.scriptDirection === 'rtl';
    });
    this.setRtl(INITIAL_LANGUAGE_KEY);

    this.resolveLabels(this.buildTranslation());

    this.translate.onLangChange.subscribe(({ lang }) => {
      this.config.language.translations[lang] = this.buildTranslation();
      this.setRtl(lang);
      this.lib.setLanguage(lang);
    });

    this.runPromise.then(() => {
      if (!this.lib.validConsent()) {
        this.lib.show(true);
      }
    });
  }

  /** Opens the preferences view, showing the current choice. */
  showPreferences(): void {
    this.lib.showPreferences();
  }

  private publishConsent(): void {
    const accepted = this.lib.acceptedCategory(ANALYTICS_CATEGORY);

    if (accepted !== this.analyticsConsent$.value) {
      this.analyticsConsent$.next(accepted);
    }
  }

  private setRtl(languageKey: string): void {
    this.config.language.rtl = this.isRtl ? [languageKey] : [];
  }

  private buildTranslation(): CookieConsent.Translation {
    // The library renders these strings as HTML, and org labels can be edited per organisation.
    const t = (tag: string): string => escapeHtml(this.translate.instant(tag));

    return {
      consentModal: {
        title: t('SB_Cookie_Banner_Title'),
        description: t('SB_Cookie_Banner_Description'),
        acceptAllBtn: t('SB_Cookie_Accept_All'),
        acceptNecessaryBtn: t('SB_Cookie_Reject_All'),
        showPreferencesBtn: t('SB_Cookie_Manage_Preferences')
      },
      preferencesModal: {
        title: t('SB_Cookie_Preferences_Title'),
        acceptAllBtn: t('SB_Cookie_Accept_All'),
        acceptNecessaryBtn: t('SB_Cookie_Reject_All'),
        savePreferencesBtn: t('SB_Cookie_Save_Preferences'),
        closeIconLabel: t('SB_Cookie_Close'),
        sections: [
          {
            title: t('SB_Cookie_Necessary_Title'),
            description: t('SB_Cookie_Necessary_Description'),
            linkedCategory: 'necessary'
          },
          {
            title: t('SB_Cookie_Analytics_Title'),
            description: t('SB_Cookie_Analytics_Description'),
            linkedCategory: ANALYTICS_CATEGORY
          }
        ]
      }
    };
  }
}
